/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import ReactMarkdown from 'react-markdown';
import { Save, X, Edit, Loader, Plus, Trash2, Copy, Check } from 'lucide-react';
import { Issue, IssueStatus } from '@shared/utils/issues';
import { useState } from 'react';

interface ReleaseDetailsProps {
  release: Issue;
  isEditing: boolean;
  isUpdating: boolean;
  editedFields: Partial<Issue>;
  statusOptions: IssueStatus[];
  getStatusColor: (status: IssueStatus) => string;
  onStartEditing: () => void;
  onCancelEditing: () => void;
  onSaveEdits: () => void;
  onFieldChange: (field: keyof Issue, value: any) => void;
  onStatusUpdate: (newStatus: IssueStatus) => void;
  onAddLink: () => void;
  onDeleteLink: (linkIndex: number) => void;
}

export function ReleaseDetails({
  release,
  isEditing,
  isUpdating,
  editedFields,
  statusOptions,
  getStatusColor,
  onStartEditing,
  onCancelEditing,
  onSaveEdits,
  onFieldChange,
  onStatusUpdate,
  onAddLink,
  onDeleteLink
}: ReleaseDetailsProps) {
  const [codeLogCopied, setCodeLogCopied] = useState(false);
  const [codeLogExpanded, setCodeLogExpanded] = useState(false);

  const handleCopyCodeLog = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (release.codeLog) {
      try {
        await navigator.clipboard.writeText(release.codeLog);
        setCodeLogCopied(true);
        setTimeout(() => setCodeLogCopied(false), 2000);
      } catch (error) {
        console.error('Failed to copy code log:', error);
      }
    }
  };

  const handleCodeLogClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCodeLogExpanded(true);
  };

  const handleCodeLogModalClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCodeLogExpanded(false);
  };

  return (
    <div style={{
      marginTop: '16px',
      paddingTop: '16px',
      borderTop: '1px solid #e0e0e0'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
        <h5 style={{ margin: 0, color: '#333', fontSize: '16px' }}>
          Release Details
        </h5>
        <div style={{ display: 'flex', gap: '8px' }}>
          {isEditing ? (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onSaveEdits();
                }}
                disabled={isUpdating}
                className="btn"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '6px 12px',
                  fontSize: '12px',
                  backgroundColor: '#28a745',
                  borderColor: '#28a745'
                }}
              >
                {isUpdating ? (
                  <>
                    <Loader size={12} style={{ animation: 'spin 1s linear infinite' }} />
                    Saving
                  </>
                ) : (
                  <>
                    <Save size={12} />
                    Save
                  </>
                )}
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onCancelEditing();
                }}
                disabled={isUpdating}
                className="btn btn-secondary"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '6px 12px',
                  fontSize: '12px'
                }}
              >
                <X size={12} />
                Cancel
              </button>
            </>
          ) : (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onStartEditing();
              }}
              className="btn"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '6px 12px',
                fontSize: '12px',
                backgroundColor: '#0066cc',
                borderColor: '#0066cc'
              }}
            >
              <Edit size={12} />
              Edit
            </button>
          )}
        </div>
      </div>
      
      <div style={{ display: 'grid', gap: '12px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px', alignItems: 'start' }}>
          <strong style={{ color: '#666' }}>ID:</strong>
          <span>{release.id}</span>
        </div>
        
        <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px', alignItems: 'start' }}>
          <strong style={{ color: '#666' }}>Title:</strong>
          {isEditing ? (
            <input
              type="text"
              value={editedFields.title !== undefined ? editedFields.title : release.title}
              onChange={(e) => onFieldChange('title', e.target.value)}
              onClick={(e) => e.stopPropagation()}
              style={{
                width: '100%',
                padding: '6px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px'
              }}
            />
          ) : (
            <span>{release.title}</span>
          )}
        </div>
        
        <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px', alignItems: 'start' }}>
          <strong style={{ color: '#666' }}>Version:</strong>
          {isEditing ? (
            <input
              type="text"
              value={editedFields.version !== undefined ? editedFields.version : release.version || ''}
              onChange={(e) => onFieldChange('version', e.target.value)}
              onClick={(e) => e.stopPropagation()}
              style={{
                width: '100%',
                padding: '6px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px'
              }}
            />
          ) : (
            <span>{release.version || 'Not specified'}</span>
          )}
        </div>
        
        <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px', alignItems: 'start' }}>
          <strong style={{ color: '#666' }}>Status:</strong>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <select
              value={release.status}
              onChange={(e) => onStatusUpdate(e.target.value as IssueStatus)}
              disabled={isUpdating}
              style={{
                padding: '4px 8px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px',
                backgroundColor: isUpdating ? '#f5f5f5' : 'white',
                cursor: isUpdating ? 'not-allowed' : 'pointer',
                color: getStatusColor(release.status),
                fontWeight: '600'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {statusOptions.map(status => (
                <option key={status} value={status} style={{ color: '#333' }}>
                  {status}
                </option>
              ))}
            </select>
            {isUpdating && (
              <Loader size={14} style={{ animation: 'spin 1s linear infinite', color: '#666' }} />
            )}
          </div>
        </div>
        
        <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px', alignItems: 'start' }}>
          <strong style={{ color: '#666' }}>Description:</strong>
          {isEditing ? (
            <textarea
              value={editedFields.description !== undefined ? editedFields.description : release.description}
              onChange={(e) => onFieldChange('description', e.target.value)}
              onClick={(e) => e.stopPropagation()}
              rows={8}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px',
                fontFamily: 'inherit',
                resize: 'vertical'
              }}
            />
          ) : (
            <div style={{ lineHeight: '1.5' }}>
              <ReactMarkdown 
                components={{
                  p: ({node, ...props}) => <p style={{ margin: '0 0 12px 0' }} {...props} />,
                  ul: ({node, ...props}) => <ul style={{ margin: '0 0 12px 0', paddingLeft: '20px' }} {...props} />,
                  ol: ({node, ...props}) => <ol style={{ margin: '0 0 12px 0', paddingLeft: '20px' }} {...props} />,
                  li: ({node, ...props}) => <li style={{ margin: '0 0 4px 0' }} {...props} />,
                  h1: ({node, ...props}) => <h4 style={{ margin: '12px 0 8px 0', fontSize: '18px', fontWeight: 'bold' }} {...props} />,
                  h2: ({node, ...props}) => <h5 style={{ margin: '12px 0 6px 0', fontSize: '16px', fontWeight: 'bold' }} {...props} />,
                  h3: ({node, ...props}) => <h6 style={{ margin: '12px 0 6px 0', fontSize: '14px', fontWeight: 'bold' }} {...props} />,
                  strong: ({node, ...props}) => <strong style={{ fontWeight: 'bold' }} {...props} />,
                  code: ({node, ...props}) => <code style={{ backgroundColor: '#f5f5f5', padding: '2px 4px', borderRadius: '3px', fontSize: '13px' }} {...props} />,
                  blockquote: ({node, ...props}) => <blockquote style={{ borderLeft: '3px solid #ddd', paddingLeft: '12px', margin: '8px 0', fontStyle: 'italic' }} {...props} />
                }}
              >
                {release.description || 'No description'}
              </ReactMarkdown>
            </div>
          )}
        </div>
        
        <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px', alignItems: 'start' }}>
          <strong style={{ color: '#666' }}>Created:</strong>
          <span>{new Date(release.createdAt).toLocaleString()}</span>
        </div>
        
        <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px', alignItems: 'start' }}>
          <strong style={{ color: '#666' }}>Updated:</strong>
          <span>{new Date(release.updatedAt).toLocaleString()}</span>
        </div>
        
        {(release.assignedTo || isEditing) && (
          <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px', alignItems: 'start' }}>
            <strong style={{ color: '#666' }}>Assigned To:</strong>
            {isEditing ? (
              <input
                type="text"
                value={editedFields.assignedTo !== undefined ? editedFields.assignedTo : release.assignedTo || ''}
                onChange={(e) => onFieldChange('assignedTo', e.target.value)}
                onClick={(e) => e.stopPropagation()}
                placeholder="email@example.com"
                style={{
                  width: '100%',
                  padding: '6px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              />
            ) : (
              <span>{release.assignedTo}</span>
            )}
          </div>
        )}
        
        {(release.estimate || isEditing) && (
          <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px', alignItems: 'start' }}>
            <strong style={{ color: '#666' }}>Estimate:</strong>
            {isEditing ? (
              <select
                value={editedFields.estimate !== undefined ? editedFields.estimate : release.estimate || 'M'}
                onChange={(e) => onFieldChange('estimate', e.target.value)}
                onClick={(e) => e.stopPropagation()}
                style={{
                  width: '150px',
                  padding: '6px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              >
                <option value="XS">XS (1-2h)</option>
                <option value="S">S (2-4h)</option>
                <option value="M">M (4-8h)</option>
                <option value="L">L (1-2d)</option>
                <option value="XL">XL (2-5d)</option>
                <option value="XXL">XXL (1+ wk)</option>
              </select>
            ) : (
              <span>{release.estimate}</span>
            )}
          </div>
        )}
        
        {(release.effort !== undefined && release.effort > 0) && (
          <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px', alignItems: 'start' }}>
            <strong style={{ color: '#666' }}>Effort Logged:</strong>
            <span>{release.effort} hours</span>
          </div>
        )}
        
        <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px', alignItems: 'start' }}>
          <strong style={{ color: '#666' }}>Plan:</strong>
          {isEditing ? (
            <textarea
              value={editedFields.plan !== undefined ? editedFields.plan : release.plan || ''}
              onChange={(e) => onFieldChange('plan', e.target.value)}
              onClick={(e) => e.stopPropagation()}
              rows={6}
              placeholder="Enter implementation plan (optional)"
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px',
                fontFamily: 'inherit',
                resize: 'vertical'
              }}
            />
          ) : release.plan ? (
            <div style={{ lineHeight: '1.5' }}>
              <ReactMarkdown 
                components={{
                  p: ({node, ...props}) => <p style={{ margin: '0 0 12px 0' }} {...props} />,
                  ul: ({node, ...props}) => <ul style={{ margin: '0 0 12px 0', paddingLeft: '20px' }} {...props} />,
                  ol: ({node, ...props}) => <ol style={{ margin: '0 0 12px 0', paddingLeft: '20px' }} {...props} />,
                  li: ({node, ...props}) => <li style={{ margin: '0 0 4px 0' }} {...props} />,
                  h1: ({node, ...props}) => <h4 style={{ margin: '12px 0 8px 0', fontSize: '18px', fontWeight: 'bold' }} {...props} />,
                  h2: ({node, ...props}) => <h5 style={{ margin: '12px 0 6px 0', fontSize: '16px', fontWeight: 'bold' }} {...props} />,
                  h3: ({node, ...props}) => <h6 style={{ margin: '12px 0 6px 0', fontSize: '14px', fontWeight: 'bold' }} {...props} />,
                  strong: ({node, ...props}) => <strong style={{ fontWeight: 'bold' }} {...props} />,
                  code: ({node, ...props}) => <code style={{ backgroundColor: '#f5f5f5', padding: '2px 4px', borderRadius: '3px', fontSize: '13px' }} {...props} />,
                  blockquote: ({node, ...props}) => <blockquote style={{ borderLeft: '3px solid #ddd', paddingLeft: '12px', margin: '8px 0', fontStyle: 'italic' }} {...props} />
                }}
              >
                {release.plan}
              </ReactMarkdown>
            </div>
          ) : (
            <span style={{ color: '#999', fontStyle: 'italic' }}>No plan</span>
          )}
        </div>
        
        {release.codeLog && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px', alignItems: 'start' }}>
              <strong style={{ color: '#666' }}>Code Log:</strong>
              <div 
                style={{ position: 'relative' }}
                onClick={handleCodeLogClick}
              >
                <button
                  onClick={handleCopyCodeLog}
                  style={{
                    position: 'absolute',
                    top: '8px',
                    right: '8px',
                    padding: '6px 12px',
                    border: '1px solid #dee2e6',
                    borderRadius: '4px',
                    backgroundColor: codeLogCopied ? '#28a745' : '#fff',
                    color: codeLogCopied ? '#fff' : '#495057',
                    cursor: 'pointer',
                    fontSize: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    zIndex: 10,
                    transition: 'all 0.2s'
                  }}
                  title={codeLogCopied ? 'Copied!' : 'Copy to clipboard'}
                >
                  {codeLogCopied ? (
                    <>
                      <Check size={14} />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy size={14} />
                      Copy
                    </>
                  )}
                </button>
                <div 
                  style={{ 
                    lineHeight: '1.5',
                    backgroundColor: '#f8f9fa',
                    padding: '12px',
                    paddingTop: '40px',
                    borderRadius: '4px',
                    border: '1px solid #dee2e6',
                    fontFamily: 'monospace',
                    fontSize: '12px',
                    whiteSpace: 'pre-wrap',
                    overflowY: 'auto',
                    overflowX: 'auto',
                    maxHeight: '300px',
                    cursor: 'pointer'
                  }}
                  onClick={handleCodeLogClick}
                  onWheel={(e) => {
                    e.stopPropagation();
                    const target = e.currentTarget;
                    const atTop = target.scrollTop === 0;
                    const atBottom = target.scrollTop + target.clientHeight >= target.scrollHeight;
                    
                    if ((e.deltaY < 0 && !atTop) || (e.deltaY > 0 && !atBottom)) {
                      e.preventDefault();
                    }
                  }}
                >
                  {release.codeLog}
                </div>
                <div style={{
                  marginTop: '8px',
                  fontSize: '12px',
                  color: '#6c757d',
                  fontStyle: 'italic',
                  textAlign: 'center'
                }}>
                  Click to expand in modal view
                </div>
              </div>
            </div>

            {/* Modal for expanded code log */}
            {codeLogExpanded && (
              <div
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 1000,
                  padding: '20px'
                }}
                onClick={handleCodeLogModalClose}
              >
                <div
                  style={{
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    padding: '24px',
                    maxWidth: '90vw',
                    maxHeight: '90vh',
                    width: '1200px',
                    display: 'flex',
                    flexDirection: 'column',
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                    <h3 style={{ margin: 0, color: '#333' }}>Release Build Log</h3>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={handleCopyCodeLog}
                        style={{
                          padding: '8px 16px',
                          border: '1px solid #dee2e6',
                          borderRadius: '4px',
                          backgroundColor: codeLogCopied ? '#28a745' : '#fff',
                          color: codeLogCopied ? '#fff' : '#495057',
                          cursor: 'pointer',
                          fontSize: '14px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          transition: 'all 0.2s'
                        }}
                      >
                        {codeLogCopied ? (
                          <>
                            <Check size={16} />
                            Copied!
                          </>
                        ) : (
                          <>
                            <Copy size={16} />
                            Copy
                          </>
                        )}
                      </button>
                      <button
                        onClick={handleCodeLogModalClose}
                        style={{
                          padding: '8px 16px',
                          border: '1px solid #dee2e6',
                          borderRadius: '4px',
                          backgroundColor: '#fff',
                          color: '#495057',
                          cursor: 'pointer',
                          fontSize: '14px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}
                      >
                        <X size={16} />
                        Close
                      </button>
                    </div>
                  </div>
                  <div
                    style={{
                      flex: 1,
                      lineHeight: '1.5',
                      backgroundColor: '#f8f9fa',
                      padding: '16px',
                      borderRadius: '4px',
                      border: '1px solid #dee2e6',
                      fontFamily: 'monospace',
                      fontSize: '13px',
                      whiteSpace: 'pre-wrap',
                      overflowY: 'auto',
                      overflowX: 'auto'
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {release.codeLog}
                  </div>
                </div>
              </div>
            )}
          </>
        )}
        
        <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px', alignItems: 'start' }}>
          <strong style={{ color: '#666' }}>Links:</strong>
          {isEditing ? (
            <div>
              {(editedFields.links || release.links || []).map((link, linkIndex) => (
                <div key={linkIndex} style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px',
                  marginBottom: '8px',
                  padding: '6px',
                  border: '1px solid #e0e0e0',
                  borderRadius: '4px',
                  backgroundColor: '#f9f9f9'
                }}>
                  <input
                    type="text"
                    value={link}
                    onChange={(e) => {
                      const currentLinks = editedFields.links || release.links || [];
                      const newLinks = [...currentLinks];
                      newLinks[linkIndex] = e.target.value;
                      onFieldChange('links', newLinks);
                    }}
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      flex: 1,
                      padding: '6px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '13px'
                    }}
                  />
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeleteLink(linkIndex);
                    }}
                    style={{
                      padding: '6px',
                      border: '1px solid #dc3545',
                      borderRadius: '4px',
                      backgroundColor: '#fff',
                      color: '#dc3545',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                    title="Delete link"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onAddLink();
                }}
                className="btn"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '6px 12px',
                  fontSize: '12px',
                  marginTop: '8px'
                }}
              >
                <Plus size={12} />
                Add Link
              </button>
            </div>
          ) : release.links && release.links.length > 0 ? (
            <div>
              {release.links.map((link, linkIndex) => (
                <div key={linkIndex} style={{ marginBottom: '4px' }}>
                  <a
                    href={link.startsWith('http') ? link : `#${link}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: '#0066cc', textDecoration: 'none' }}
                    onMouseOver={(e) => e.currentTarget.style.textDecoration = 'underline'}
                    onMouseOut={(e) => e.currentTarget.style.textDecoration = 'none'}
                  >
                    {link}
                  </a>
                </div>
              ))}
            </div>
          ) : (
            <span style={{ color: '#999', fontStyle: 'italic' }}>No links</span>
          )}
        </div>
        
        {((release.labels && release.labels.length > 0) || isEditing) && (
          <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px', alignItems: 'start' }}>
            <strong style={{ color: '#666' }}>Labels:</strong>
            {isEditing ? (
              <input
                type="text"
                value={(editedFields.labels !== undefined ? editedFields.labels : release.labels || []).join(', ')}
                onChange={(e) => {
                  const value = e.target.value;
                  const labels = value ? value.split(',').map(l => l.trim()).filter(l => l) : [];
                  onFieldChange('labels', labels);
                }}
                onClick={(e) => e.stopPropagation()}
                placeholder="e.g., critical, frontend, backend"
                style={{
                  width: '100%',
                  padding: '6px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              />
            ) : (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {release.labels!.map((label, labelIndex) => (
                  <span
                    key={labelIndex}
                    style={{
                      padding: '2px 8px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      backgroundColor: '#e9ecef',
                      color: '#495057'
                    }}
                  >
                    {label}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      
      <div style={{ 
        marginTop: '12px', 
        fontSize: '12px', 
        color: '#999',
        textAlign: 'center'
      }}>
        Click anywhere on the release card to collapse
      </div>
    </div>
  );
}
